// Connects the chord source (microphone, taps, or the clock itself) to the
// dock's engine and keeps track of where in the song the player is, within
// the whole song or a part of it being looped.

import { parseChord, transposeChord } from './chords';
import MicInput, { ChordTracker, SongFollower, SPEAKER_OPTIONS } from './listener';

// After this many beats without hearing the guitar the bass stops too.
const SILENCE_BEATS = 6;
// A chord heard for less than this many beats before going back to the one
// before it doesn't move the marker through the song.
const BLIP_BEATS = 1.5;

export default class JamSession {
    // engine: the dock's BackingTrackEngine, shared with the metronome.
    // onPass: called each time round a looped part.
    // capo: the fret the player's capo is on (0 for none). The chords are
    // written as the shapes played, so they sound that many semitones
    // higher: that's what the listener listens for and the bass plays.
    constructor({ onChange, onPass, engine, capo = 0 }) {
        this.onChange = onChange;
        this.onPass = onPass;
        this.engine = engine;
        this.capo = capo;
        this.mic = null;
        this.pendingMic = null;
        // Direction of a step being taken (tap mode), for counting loops.
        this.stepping = 0;
        this.micState = 'off';
        this.headphones = false;
        this.current = null;
        this.level = 0;
        this.lastHeard = 0;
        // Part of the song being looped: { start, end } positions in
        // this.sequence, inclusive. null is the whole song.
        this.section = null;
        // While the chords play by themselves: the position being played.
        this.auto = false;
        this.autoPosition = -1;
        this.lastPosition = -1;
        this.setSong([]);
    }

    // sequence: chord symbols in the order they appear in the song.
    setSong(sequence) {
        const names = [];
        const indexes = [];
        sequence.forEach(name => {
            if (!parseChord(name)) return;
            if (!names.includes(name)) names.push(name);
            indexes.push(names.indexOf(name));
        });
        this.names = names;
        this.chords = this.soundingChords();
        this.sequence = indexes;
        // Transposing keeps the chords where they are, so a loop survives it.
        if (this.section && this.section.end >= indexes.length) this.section = null;
        this.buildFollower();
        this.buildTracker();
        this.current = null;
        this.engine.setChord(null);
        this.emit();
    }

    // The song's chords as they sound (see capo), by index into this.names.
    soundingChords() {
        return this.names.map(name => transposeChord(parseChord(name), this.capo));
    }

    // Putting the capo on or off keeps the place in the song.
    setCapo(capo) {
        if (capo === this.capo) return;
        this.capo = capo;
        this.chords = this.soundingChords();
        this.buildTracker();
        if (this.current === null) return;
        const next = this.auto ? this.sequence[this.autoNextPosition()] : this.follower.expectedNext();
        this.tracker.setHint(next);
        this.engine.setChord(this.chords[this.current], next === null || next === this.current ? null : this.chords[next]);
    }

    buildFollower() {
        const { start, end } = this.range();
        this.follower = new SongFollower(this.sequence.slice(start, end + 1));
        this.autoPosition = -1;
        this.lastPosition = -1;
        this.lastRun = -1;
        // Set once the player gets near the end of a looped part, so going
        // back to its top counts as one time round (once).
        this.passArmed = false;
    }

    range() {
        return this.section || { start: 0, end: this.sequence.length - 1 };
    }

    // Loop part of the song (null for all of it), starting from its top.
    setSection(section) {
        this.section = section;
        this.buildFollower();
        this.current = null;
        this.engine.setChord(null);
        this.emit();
    }

    // Back to the top of the song (or the looped part).
    restart() {
        this.buildFollower();
        this.current = null;
        this.tracker.reset();
        this.engine.setChord(null);
        this.emit();
    }

    // Position in this.sequence of the chord being played, or -1.
    position() {
        if (this.auto) return this.autoPosition;
        const p = this.follower.position;
        return p < 0 ? -1 : p + this.range().start;
    }

    buildTracker() {
        this.tracker = new ChordTracker(this.chords, this.sequence, this.headphones ? {} : SPEAKER_OPTIONS);
    }

    emit() {
        const position = this.position();
        if (!this.auto && position !== this.lastPosition) {
            // From the end of a looped part forward round to its top: one or
            // two chord changes on (one in a short loop, where two forward is
            // also one back), and never by stepping backwards.
            const { runs, run } = this.follower;
            const n = runs.length;
            if (this.section && run >= 0 && this.lastRun >= 0) {
                const forward = (run - this.lastRun + n) % n;
                if (this.passArmed && run < this.lastRun && this.stepping >= 0 && forward >= 1 && forward <= (n > 4 ? 2 : 1)) {
                    this.passArmed = false;
                    this.pass();
                }
            }
            if (run >= n - (n > 4 ? 2 : 1)) this.passArmed = true;
            this.lastRun = run;
        }
        this.lastPosition = position;
        if (this.onChange) {
            const next = this.auto ? this.autoNextPosition() : -1;
            this.onChange({
                chord: this.current === null ? null : this.names[this.current],
                next: this.expectedNextName(),
                position,
                nextPosition: next,
                micState: this.micState,
                level: this.level,
            });
        }
    }

    expectedNextName() {
        if (this.auto) {
            const next = this.sequence[this.autoNextPosition()];
            return next === undefined ? null : this.names[next];
        }
        const next = this.follower.expectedNext();
        return next === null ? null : this.names[next];
    }

    pass(time) {
        if (this.onPass) this.onPass();
        // Speeding up each time round is for the clock; following alone has none.
        const { ramp } = this.engine;
        if (ramp && !ramp.everyBars && this.engine.playing) this.engine.bumpTempo(time);
    }

    // Let the clock move through the chords, `beats` beats each (null hands
    // them back to the microphone or taps). Changing how long chords last
    // keeps the place in the song.
    setAuto(beats) {
        if (!beats || !this.auto) this.autoPosition = -1;
        this.auto = !!beats;
        this.engine.setAutoChords(beats ? { beats, next: time => this.autoNext(time) } : null);
        this.emit();
    }

    autoNextPosition() {
        const { start, end } = this.range();
        return this.autoPosition < start || this.autoPosition >= end ? start : this.autoPosition + 1;
    }

    // Called by the engine, ahead of time, when the next chord is due.
    autoNext(time) {
        if (this.sequence.length === 0) return { chord: null, nextChord: null };
        const { end } = this.range();
        const wrapped = this.autoPosition === end;
        this.autoPosition = this.autoNextPosition();
        const index = this.sequence[this.autoPosition];
        const next = this.sequence[this.autoNextPosition()];
        this.current = index;
        this.tracker.setHint(next);
        this.engine.at(time, () => {
            if (wrapped && this.section) this.pass(time);
            this.emit();
        });
        return { chord: this.chords[index], nextChord: next === index ? null : this.chords[next] };
    }

    async startListening({ headphones }) {
        this.stopListening();
        this.headphones = headphones;
        this.buildTracker();
        if (!MicInput.isSupported()) {
            this.micState = 'unsupported';
            this.emit();
            return;
        }
        this.micState = 'requesting';
        this.emit();
        const ctx = this.engine.ensureContext();
        const mic = new MicInput(ctx, frame => this.onFrame(frame));
        this.pendingMic = mic;
        try {
            await mic.start({ headphones });
            if (this.pendingMic !== mic) {
                mic.stop(); // stopped (or restarted) while the browser asked
                return;
            }
            this.mic = mic;
            this.micState = 'on';
        } catch (e) {
            if (this.pendingMic !== mic) return;
            this.micState = e && e.name === 'NotAllowedError' ? 'denied' : 'error';
        }
        this.pendingMic = null;
        this.emit();
    }

    get listening() {
        return !!(this.mic || this.pendingMic);
    }

    stopListening() {
        if (this.pendingMic) {
            this.pendingMic.stop();
            this.pendingMic = null;
        }
        if (this.mic) {
            this.mic.stop();
            this.mic = null;
        }
        if (this.micState !== 'off') {
            this.micState = 'off';
            this.emit();
        }
    }

    onFrame({ rms, active, chroma }) {
        this.level = rms;
        const now = performance.now();
        if (active) {
            this.lastHeard = now;
            const index = this.tracker.update(chroma);
            if (index !== null && index !== this.current) {
                this.select(index, { follow: true });
                return;
            }
        } else if (this.current !== null && now - this.lastHeard > SILENCE_BEATS * 60000 / this.engine.bpm) {
            this.current = null;
            this.tracker.reset();
            this.engine.setChord(null);
        }
        this.emit();
    }

    // Play over chord `index` (into this.names). follow: work out where in
    // the song that puts the player (see SongFollower).
    select(index, { follow = false } = {}) {
        this.current = index;
        if (follow) {
            this.follower.blipWindow = BLIP_BEATS * 60 / this.engine.bpm;
            this.follower.hear(index, performance.now() / 1000);
        }
        const next = this.follower.expectedNext();
        this.tracker.setHint(next);
        this.engine.setChord(this.chords[index], next === null ? null : this.chords[next]);
        this.emit();
    }

    // Tap mode: step through the song's chords in order (e.g. with a pedal).
    step(delta) {
        if (this.sequence.length === 0) return;
        const position = this.follower.step(delta);
        this.stepping = delta;
        this.select(this.sequence[position + this.range().start]);
        this.stepping = 0;
    }

    // Tap mode: a chord picked by hand (its button or number key). The
    // marker goes to the next place that chord comes after it in the song
    // (or the looped part); a tap is the player saying where they are, not
    // a sound to second-guess the way the listener's chords are.
    selectByName(name) {
        const index = this.names.indexOf(name);
        if (index === -1) return;
        const follower = this.follower;
        const n = follower.runs.length;
        let target = -1;
        for (let k = 1; k <= n && target === -1; k++) {
            const run = (follower.run + k + n) % n;
            if (follower.chords[run] === index) target = run;
        }
        if (target === -1) {
            this.select(index); // not in the looped part: play it, marker stays
            return;
        }
        if (follower.run === -1) follower.step(1);
        const delta = target - follower.run;
        if (delta !== 0) follower.step(delta);
        this.stepping = 1;
        this.select(index);
        this.stepping = 0;
    }

    // The engine is the dock's and outlives the session.
    close() {
        this.onChange = null;
        this.stopListening();
        this.engine.setAutoChords(null);
    }
}
